/**
 * Shaved Doji chart overlay (R144) — CLH-close stop-and-reverse.
 *
 * BUY  when CLH close > previous CLH close → state LONG  (covers any short)
 * SHORT when CLH close < previous CLH close → state SHORT (sells any long)
 * equal close ⇒ carry. A signal fires only on the FLIP; candles are coloured
 * by the running state (LONG green / SHORT red / FLAT neutral). Uses CLH
 * closes only, computed from the RAW candles so it's identical on any display
 * candle type. Mirrors backend/src/services/strategies/shavedDojiRules.ts.
 */
import { CandleRow } from '../../components/charts/TradingChart';
import { ChartStrategy, StrategyOverlayResult, CandleColor, ChartMarker } from './types';
import { transformCandles } from '../candleTransforms';

const GREEN = '#26a69a';   // candle body (position-state) colour
const RED = '#ef5350';
const NEUTRAL = '#787b86';
// R146 — uniform entry-marker palette (matches the RockBand benchmark).
const MARK_BUY = '#00C853';
const MARK_SHORT = '#D50000';

export function computeShavedDojiOverlay(candles: CandleRow[]): StrategyOverlayResult {
  if (!candles || candles.length < 2) return { candleColors: [], markers: [] };
  const clh = transformCandles(candles, 'CLH'); // preserves length + bar time

  const candleColors: CandleColor[] = [];
  const markers: ChartMarker[] = [];
  const signalsByTime: NonNullable<StrategyOverlayResult['signalsByTime']> = new Map();
  const positionByTime: NonNullable<StrategyOverlayResult['positionByTime']> = new Map();

  let state: 'LONG' | 'SHORT' | 'FLAT' = 'FLAT';
  let prevDir = 0;
  for (let i = 0; i < clh.length; i++) {
    let dir = prevDir;
    if (i >= 1) {
      const c = Number(clh[i].close), p = Number(clh[i - 1].close);
      if (Number.isFinite(c) && Number.isFinite(p)) {
        if (c > p) dir = 1; else if (c < p) dir = -1;
      }
    }
    const t = candles[i].time;
    if (dir === 1 && state !== 'LONG') {
      state = 'LONG';
      markers.push({ time: t, position: 'belowBar', text: 'B', color: MARK_BUY, shape: 'arrowUp', size: 2 });
      signalsByTime.set(t, { signal: 'BUY', strength: null, pos: 'LONG' });
    } else if (dir === -1 && state !== 'SHORT') {
      state = 'SHORT';
      markers.push({ time: t, position: 'aboveBar', text: 'S', color: MARK_SHORT, shape: 'arrowDown', size: 2 });
      signalsByTime.set(t, { signal: 'SHORT', strength: null, pos: 'SHORT' });
    }
    const col = state === 'LONG' ? GREEN : state === 'SHORT' ? RED : NEUTRAL;
    candleColors.push({ time: t, upColor: col, downColor: col });
    positionByTime.set(t, state);
    prevDir = dir;
  }
  return { candleColors, markers, signalsByTime, positionByTime };
}

export const shavedDojiStrategy: ChartStrategy = {
  id: 'shaved_doji',
  name: 'Shaved Doji',
  computeOverlays(candles: CandleRow[]): StrategyOverlayResult {
    return computeShavedDojiOverlay(candles);
  },
};
